import type { EnumUnion } from "@dresscode-frontend/utility-types";
import type { CreateSoftwareReq } from "./-api";

export const SoftwareTypeEnum = {
  INSTALLED: "INSTALLED",
  MW_OS: "MW_OS",
  SCRATCH: "SCRATCH",
  PRODUCT: "PRODUCT",
  SAAS: "SAAS",
  CUSTOM_SAAS: "CUSTOM_SAAS",
} as const satisfies Record<
  string,
  NonNullable<CreateSoftwareReq["info"]["type"]>
>;

export const SoftwareCategoryEnum = {
  MARKETING: "MARKETING",
  SALES: "SALES",
  CS: "CS",
  RESEARCH: "RESEARCH",
  HR: "HR",
  ACCOUNTING_FINANCE: "ACCOUNTING_FINANCE",
  LEGAL: "LEGAL",
  OPERATION_WORKFLOW: "OPERATION_WORKFLOW",
  SUSTAINABILITY_IR: "SUSTAINABILITY_IR",
  COMMUNICATION: "COMMUNICATION",
  GROUPWARE_STORAGE: "GROUPWARE_STORAGE",
  KNOWLEDGE_DOCUMENT: "KNOWLEDGE_DOCUMENT",
  PRODUCTIVITY: "PRODUCTIVITY",
  DEV_OPS: "DEV_OPS",
  PROJECT_MANAGEMENT: "PROJECT_MANAGEMENT",
  AI: "AI",
  RPA_IPAAS: "RPA_IPAAS",
  WEBSITE: "WEBSITE",
  ANALYTICS: "ANALYTICS",
  SECURITY_COMPLIANCE: "SECURITY_COMPLIANCE",
  OTHER: "OTHER",
} as const satisfies Record<
  string,
  NonNullable<CreateSoftwareReq["info"]["category"]>
>;

export const SoftwareUsageStatusEnum = {
  ACTIVE: "ACTIVE",
  TRIAL: "TRIAL",
  DEACTIVATED: "DEACTIVATED",
  DISCONTINUED: "DISCONTINUED",
} as const satisfies Record<
  string,
  NonNullable<CreateSoftwareReq["operationalStatus"]["usageStatus"]>
>;

// パッチステータス
export const PatchStatusEnum = {
  PATCHED: "PATCHED",
  SCHEDULED: "SCHEDULED",
  NOT_PATCHED: "NOT_PATCHED",
} as const satisfies Record<
  string,
  NonNullable<CreateSoftwareReq["versionAndBuild"]["patchStatus"]>
>;

// インスタンスタイプ
export const InstanceTypeEnum = {
  SAAS_TENANT: "SAAS_TENANT",
  ON_PREMISE: "ON_PREMISE",
  DEV: "DEV",
  TEST: "TEST",
  PROD: "PROD",
} as const satisfies Record<
  string,
  NonNullable<CreateSoftwareReq["instance"]["instanceType"]>
>;

// 環境タイプ
export const EnvironmentEnum = {
  DEV: "DEV",
  STG: "STG",
  PROD: "PROD",
} as const satisfies Record<
  string,
  NonNullable<CreateSoftwareReq["instance"]["environment"]>
>;

// インストール手法
export const InstallationMethodEnum = {
  MANUAL: "MANUAL",
  MDM: "MDM",
  INSTALLER: "INSTALLER",
} as const satisfies Record<
  string,
  NonNullable<CreateSoftwareReq["installation"]["installationMethod"]>
>;

export const UninstallationMethodEnum = {
  MANUAL: "MANUAL",
  MDM: "MDM",
  INSTALLER: "INSTALLER",
} as const satisfies Record<
  string,
  NonNullable<CreateSoftwareReq["uninstallation"]["uninstallationMethod"]>
>;

// 自動更新設定
export const AutoUpdateSettingEnum = {
  ENABLED: "ENABLED",
  DISABLED: "DISABLED",
  MANUAL: "MANUAL",
} as const satisfies Record<
  string,
  NonNullable<
    CreateSoftwareReq["installationSecurityAndCompliance"]["autoUpdateSetting"]
  >
>;

export const DeploymentMethodEnum = {
  CLOUD_BASED: "CLOUD_BASED",
  ON_PREMISE: "ON_PREMISE",
  HYBRID: "HYBRID",
} as const satisfies Record<
  string,
  NonNullable<
    CreateSoftwareReq["provisioningAndDistribution"]["deploymentMethod"]
  >
>;

export const GdprComplianceEnum = {
  COMPLIANT: "COMPLIANT",
  IN_PROGRESS: "IN_PROGRESS",
  NON_COMPLIANT: "NON_COMPLIANT",
} as const satisfies Record<
  string,
  NonNullable<CreateSoftwareReq["securityAndCompliance"]["gdprCompliance"]>
>;

export const AccessControlTypeEnum = {
  SAML: "SAML",
  OAUTH: "OAUTH",
  SELF: "SELF",
  OTHER: "OTHER",
} as const satisfies Record<
  string,
  NonNullable<CreateSoftwareReq["securityAndCompliance"]["accessControlType"]>
>;

export const UsageStatusEnum = {
  ACTIVE: "ACTIVE",
  TRIAL: "TRIAL",
  DEACTIVATED: "DEACTIVATED",
  DISCONTINUED: "DISCONTINUED",
} as const satisfies Record<
  string,
  NonNullable<CreateSoftwareReq["operationalStatus"]["usageStatus"]>
>;

export type SoftwareType = EnumUnion<typeof SoftwareTypeEnum>;
export type SoftwareCategory = EnumUnion<typeof SoftwareCategoryEnum>;
export type SoftwareUsageStatus = EnumUnion<typeof SoftwareUsageStatusEnum>;
export type PatchStatus = EnumUnion<typeof PatchStatusEnum>;
export type InstanceType = EnumUnion<typeof InstanceTypeEnum>;
export type Environment = EnumUnion<typeof EnvironmentEnum>;
export type InstallationMethod = EnumUnion<typeof InstallationMethodEnum>;
export type AutoUpdateSetting = EnumUnion<typeof AutoUpdateSettingEnum>;
export type DeploymentMethod = EnumUnion<typeof DeploymentMethodEnum>;
export type GdprCompliance = EnumUnion<typeof GdprComplianceEnum>;
export type AccessControlType = EnumUnion<typeof AccessControlTypeEnum>;
export type UsageStatus = EnumUnion<typeof UsageStatusEnum>;
export type UninstallationMethod = EnumUnion<typeof UninstallationMethodEnum>;

/**
 * 型ガード関数: 値がSoftwareType型かどうかを判定
 * APIから返される値の型安全性を確保するために使用
 */
export function isSoftwareType(value: unknown): value is SoftwareType {
  return (
    typeof value === "string" &&
    Object.values(SoftwareTypeEnum).includes(value as SoftwareType)
  );
}
